// Locale-aware number/currency formatting helpers, kept separate from
// components so they stay trivially unit-testable.
//
// `locale` is the app's active UI language code (see src/i18n/languages.ts,
// e.g. "sl", "de", "ar") - passing just the bare language subtag (no region)
// to Intl.NumberFormat is intentional: the browser picks a sensible default
// grouping/decimal convention for that language, and EUR's own symbol/
// placement still follows each locale's normal currency-formatting rules
// (e.g. "10,00 €" in German, "€10.00" in English). Every call site should
// pass the current language from useLanguage(); the "en" default only
// covers code paths that run outside the LanguageProvider (there should be
// none in practice, but this avoids a hard crash if that ever changes).

export function formatClicks(clicks: number, locale: string = "en"): string {
  return new Intl.NumberFormat(locale, { useGrouping: true }).format(Math.max(0, Math.trunc(clicks)));
}

/** Formats an integer number of cents as a locale-formatted EUR amount, e.g. 1234 -> "€12.34" (or "12,34 €" in German). */
export function formatEuroFromCents(cents: number, locale: string = "en"): string {
  const euros = cents / 100;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(euros);
}

/** Parses a user-typed amount string ("12.34", or "12,34" - still accepted leniently since a comma decimal is a common habit for EU visitors) into integer cents, or null if invalid. */
export function parseEuroInputToCents(input: string): number | null {
  const normalized = input.trim().replace(/\s/g, "").replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const [wholePart, fractionPart = ""] = normalized.split(".");
  const cents = Number(wholePart) * 100 + Number(fractionPart.padEnd(2, "0"));
  return Number.isSafeInteger(cents) ? cents : null;
}
